import React, { useEffect, useState, useContext } from "react";
import { useSelector } from "react-redux";
import { Drawer, Form, Input, Select, Checkbox, notification } from "antd";

import ServiceContext from "./ServiceContext";

import {
  useEcommerceCategoriesOptions,
  useEmployersOptions,
} from "../../../../hooks/options";

import REQUESTS from "../../../../server/requests";

import EN from "../../../../config/en";
import ICONS from "../../../../config/icons";

import ErrorMessage from "../../../elements/ErrorMessage";
import SaveButtonComponent from "../../../elements/SaveButtonComponent";
import FileChooser from "../../../elements/FileChooser";
import Translation from "../../../elements/Translation";
import TagComponent from "../../../elements/TagComponent";

export default function ServiceDrawer() {
  const {
    visibleServiceDrawer,
    setVisibleServiceDrawer,
    currentService,
    getServicesList,
  } = useContext(ServiceContext);

  const translate = useSelector((state) => state.globalState.translate);

  const categoriesOptions = useEcommerceCategoriesOptions();

  const employersOptions = useEmployersOptions();

  const [loading, setLoading] = useState(false);

  const [errorText, setErrorText] = useState(null);

  const [imageSrc, setImageSrc] = useState(null);

  const [form] = Form.useForm();

  const [nameTranslations, setNameTranslations] = useState([]);
  const [descriptionTranslations, setDescriptionTranslations] = useState([]);

  useEffect(() => {
    if (visibleServiceDrawer) {
      if (currentService) {
        form.setFields([
          { name: "attach_slider", value: currentService.attach_slider },
          { name: "price", value: currentService.price },
          { name: "category_id", value: currentService.category_id },
          { name: "image", value: currentService.image },
          { name: "employer_id", value: currentService?.employer?.id },
          { name: "tags", value: currentService.tags },
        ]);

        setNameTranslations(currentService.name_translations || []);

        setDescriptionTranslations(currentService.description_translations || []);

        if (currentService.image) {
          setImageSrc(currentService.image);
        }
      }
    } else {
      setErrorText(null);
      setDescriptionTranslations([]);
      setNameTranslations([]);
    }
  }, [visibleServiceDrawer]);

  useEffect(() => {
    if (!visibleServiceDrawer && currentService) {
      form.resetFields();
      setImageSrc(null);
    }
  }, [visibleServiceDrawer]);

  const onFinish = (values) => {
    setLoading(true);

    setErrorText(null);

    const body = {
      attach_slider: values.attach_slider,
      price: values.price,
      employer_id: values.employer_id,
      category_id: values.category_id,
      image: values.image,
      name: JSON.stringify(nameTranslations || []),
      description: JSON.stringify(descriptionTranslations || []),
      tags: values.tags?.join(","),
    };

    function callback() {
      setLoading(false);
      setVisibleServiceDrawer(false);
      getServicesList();
      form.resetFields();
      setImageSrc(null);
    }

    function errorCallback(err) {
      // setErrorText(err);

      notification.error({
        message:
          typeof err === "string"
            ? err
            : translate["An error occurred"] || EN["An error occurred"],
      });
      setLoading(false);
    }

    if (currentService) {
      body.id = currentService.id;

      REQUESTS.ECOMMERCE.SERVICES.EDIT(body, callback, errorCallback);
    } else {
      REQUESTS.ECOMMERCE.SERVICES.ADD(body, callback, errorCallback);
    }
  };

  const onFinishFailed = () => {
    setLoading(false);
    setErrorText(null);
  };

  const selectFile = (item) => {
    setImageSrc(item.file_url);
    form.setFields([{ name: "image", value: item.file_url }]);
  };

  const repliceImage = () => {
    setImageSrc(null);
    form.setFields([{ name: "image", value: null }]);
  };

  const onChangeName = (text, id) => {
    const name = {
      text,
      language_id: id,
    };

    const newTranslations = [...nameTranslations];

    const i = newTranslations.findIndex((i) => i.language_id === id);

    if (i >= 0) {
      newTranslations[i] = name;
    } else {
      newTranslations.push(name);
    }

    setNameTranslations(newTranslations);
  };

  const onChangeDesctiption = (text, id) => {
    const description = {
      text,
      language_id: id,
    };

    const newTranslations = [...descriptionTranslations];

    const i = newTranslations.findIndex((i) => i.language_id === id);

    if (i >= 0) {
      newTranslations[i] = description;
    } else {
      newTranslations.push(description);
    }

    setDescriptionTranslations(newTranslations);
  };

  return (
    <Drawer
      title={
        currentService
          ? translate["Edit Service"] || EN["Edit Service"]
          : translate["Add New Service"] || EN["Add New Service"]
      }
      placement="right"
      width={600}
      onClose={() => {
        setVisibleServiceDrawer(false);
        form.resetFields();
        setImageSrc(null);
      }}
      visible={visibleServiceDrawer}
      destroyOnClose={true}
    >
      <h2>{translate["Service Information"] || EN["Service Information"]}</h2>

      <Form
        form={form}
        layout="vertical"
        name="service-form"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        initialValues={{
          attach_slider: false,
        }}
      >
        <Form.Item
          label={translate["Image"] || EN["Image"]}
          tooltip={{
            title: "Choose an image",
            icon: ICONS.INFO,
          }}
          name="image"
          rules={[
            {
              required: true,
              message: `${translate["Please choose"] || EN["Please choose"]} ${
                translate["Image"] || EN["Image"]
              }`,
            },
          ]}
        >
          <FileChooser
            src={imageSrc}
            onChange={selectFile}
            replace={repliceImage}
            types={["image"]}
          />
        </Form.Item>
        {visibleServiceDrawer && (
          <Translation
            render={(item, index) => {
              const name = nameTranslations.find(
                (translation) => translation.language_id === item.id
              )?.text;
              const description = descriptionTranslations.find(
                (translation) => translation.language_id === item.id
              )?.text;

              return (
                <div>
                  <Form.Item
                    label={translate["Name"] || EN["Name"]}
                    tooltip={{
                      title: "Service name",
                      icon: ICONS.INFO,
                    }}
                    rules={[
                      {
                        required: true,
                        message: `${translate["Please input"] || EN["Please input"]} ${
                          translate["Name"] || EN["Name"]
                        }`,
                      },
                    ]}
                  >
                    <Input
                      value={name}
                      onChange={(e) => onChangeName(e.target.value, item.id)}
                    />
                  </Form.Item>

                  <Form.Item
                    label={translate["Description"] || EN["Description"]}
                    tooltip={{
                      title: "Service description",
                      icon: ICONS.INFO,
                    }}
                    rules={[
                      {
                        required: true,
                        message: `${translate["Please input"] || EN["Please input"]} ${
                          translate["Description"] || EN["Description"]
                        }`,
                      },
                    ]}
                  >
                    <Input.TextArea
                      value={description}
                      onChange={(e) => onChangeDesctiption(e.target.value, item.id)}
                      rows={5}
                    />
                  </Form.Item>
                </div>
              );
            }}
          />
        )}

        <Form.Item
          label={translate["Price"] || EN["Price"]}
          tooltip={{
            title: "Service price",
            icon: ICONS.INFO,
          }}
          name="price"
          rules={[
            {
              required: true,
              message: `${translate["Please input"] || EN["Please input"]} ${
                translate["Price"] || EN["Price"]
              }`,
            },
            {
              pattern: new RegExp(/^\d+$/),
              message:
                translate["Price must be a number"] || EN["Price must be a number"],
            },
          ]}
        >
          <Input type="text" />
        </Form.Item>

        <Form.Item
          label={translate["Users"] || EN["Users"]}
          tooltip={{
            title: "Select user",
            icon: ICONS.INFO,
          }}
          name="employer_id"
          rules={[
            {
              required: true,
              message: `${translate["Please select"] || EN["Please select"]} ${
                translate["User"] || EN["User"]
              }`,
            },
          ]}
        >
          <Select
            style={{ width: "100%" }}
            showArrow
            allowClear={true}
            options={employersOptions}
          />
        </Form.Item>

        <Form.Item
          label={translate["Category"] || EN["Category"]}
          tooltip={{
            title: "Choose category",
            icon: ICONS.INFO,
          }}
          name="category_id"
          rules={[
            {
              required: true,
              message: `${translate["Please select"] || EN["Please select"]} ${
                translate["Category"] || EN["Category"]
              }`,
            },
          ]}
        >
          <Select
            style={{ width: "100%" }}
            showArrow
            allowClear={true}
            options={categoriesOptions}
          />
        </Form.Item>

        <TagComponent type="e_commercial" form={form} />

        <Form.Item name="attach_slider" valuePropName="checked">
          <Checkbox style={{ marginBottom: 25, color: "#abb0c0" }}>
            {translate["Attach Slider"] || EN["Attach Slider"]}
          </Checkbox>
        </Form.Item>

        <Form.Item>
          <SaveButtonComponent loading={loading} onClick={() => form.submit()} />
        </Form.Item>
      </Form>

      {/* <ErrorMessage>{errorText}</ErrorMessage> */}
    </Drawer>
  );
}
